
import { motion } from 'framer-motion';
import { AlertCircle, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AnalyzingStateProps {
  progress?: number;
  error?: string | null;
  onComeBackLater?: () => void;
}

export function AnalyzingState({ progress = 0, error, onComeBackLater }: AnalyzingStateProps) {
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-8">
        <AlertCircle className="w-16 h-16 text-red-500" />
        <div className="space-y-3 text-center px-4">
          <h1 className="text-3xl font-bold text-gray-900">
            Research Failed
          </h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-8">
      {/* Animated search icon */}
      {/* @ts-ignore - Framer Motion types compatibility */}
      <motion.div
        className="relative"
        {...({
          animate: {
            scale: [1, 1.1, 1],
          },
          transition: {
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut",
          },
        } as any)}
      >
        <div className="w-32 h-16 border-4 border-black rounded-full relative">
          {/* @ts-ignore - Framer Motion types compatibility */}
          <motion.div
            className="absolute right-2 top-1/2 transform -translate-y-1/2"
            {...({
              animate: {
                x: [0, 5, 0],
              },
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              },
            } as any)}
          >
            <div className="w-6 h-6 bg-black rounded-full"></div>
          </motion.div>
        </div>
      </motion.div>

      {/* Text */}
      <div className="space-y-3 text-center px-4">
        <h1 className="text-3xl font-casual font-bold text-gray-900">
          Deep research in progress...
        </h1>
        <p className="text-gray-600">
          This may take a few minutes as we analyze multiple sources
        </p>
      </div>

      {/* Progress Bar */}
      <div className="w-full max-w-md px-4">
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          {/* @ts-ignore - Framer Motion types compatibility */}
          <motion.div
            className="h-full bg-black"
            {...({
              initial: { width: 0 },
              animate: { width: `${progress}%` },
              transition: { duration: 0.3 },
            } as any)}
          />
        </div>
        <p className="text-center text-sm text-gray-500 mt-2">
          {progress}% complete
        </p>
      </div>

      {/* Come back later section */}
      {onComeBackLater && (
        // @ts-ignore - Framer Motion types compatibility
        <motion.div
          className="mt-8 flex flex-col items-center space-y-4 px-4"
          {...({
            initial: { opacity: 0, y: 20 },
            animate: { opacity: 1, y: 0 },
            transition: { delay: 2, duration: 0.5 },
          } as any)}
        >
          <div className="flex items-center gap-2 text-gray-500">
            <Clock className="w-4 h-4" />
            <span className="text-sm">This can take 3-4 minutes</span>
          </div>
          <Button
            variant="outline"
            onClick={onComeBackLater}
            className="group border-gray-300 hover:border-black hover:bg-gray-50 transition-all"
          >
            <span>Come back later</span>
            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
          </Button>
          <p className="text-xs text-gray-400 text-center max-w-xs">
            We'll save the results in your history. You can check back anytime.
          </p>
        </motion.div>
      )}
    </div>
  );
}
